import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api, type Operation, type Station } from '../lib/api.ts';
import { useAuth } from '../App.tsx';
import { formatDuration, formatDecimalHours, formatMinutes, todayInPlant } from '../../shared/time.ts';
import { policz } from '../../shared/pl.ts';
import { Blad, Kpi, Odznaka, Panel, Pusto, Tabela, ZnacznikSymulacji, type Kolumna } from '../ui/index.tsx';

/**
 * Rejestr pracy — najgęstszy ekran aplikacji.
 *
 * Uwaga nazewnicza: „Zlecenia" to u nas zlecenia produkcyjne (ZP) z marszrutą
 * i numerami seryjnymi. Tabela „kto, co, kiedy, ile" to osobny byt i nazywa się
 * Praca. Mieszanie tych dwóch encji było źródłem nieporozumień.
 */

interface Wiersz {
  id: number; work_date: string; employee_id: number; login: string;
  station_code: string; operations: string[]; zp_number: string | null; serials: string[];
  start_min: number; end_min: number;
  is_prototype: number; is_finished: number; is_symulacja: number; is_backdated: number;
  notes: string | null;
  workedMin: number; creditMin: number; efficiency: number | null; brakNormy: boolean;
}
interface Odp { od: string; do: string; wiersze: Wiersz[]; plantTime: string }

export default function Praca() {
  const { user } = useAuth();
  const kierownik = user?.role !== 'worker';
  const [params, setParams] = useSearchParams();

  const od = params.get('od') ?? przesun(todayInPlant(), -6);
  const doD = params.get('do') ?? todayInPlant();
  const stanowisko = params.get('stanowisko') ?? '';
  const operacja = params.get('operacja') ?? '';
  const szukaj = params.get('q') ?? '';
  // Filtr po pracowniku nie ma własnego pola — wchodzi się w niego z Analityki
  // albo z karty pracownika, więc wystarczy zdejmowalny znacznik.
  const pracownik = params.get('pracownik') ?? '';

  const [d, setD] = useState<Odp | null>(null);
  const [blad, setBlad] = useState<string | null>(null);
  const [laduje, setLaduje] = useState(true);
  const [stacje, setStacje] = useState<Station[]>([]);
  const [operacje, setOperacje] = useState<Operation[]>([]);
  const [otwarty, setOtwarty] = useState<Wiersz | null>(null);

  useEffect(() => {
    api.get<{ stations: Station[]; operations: Operation[] }>('/catalog')
      .then((r) => { setStacje(r.stations); setOperacje(r.operations); });
  }, []);

  const load = () => {
    setLaduje(true);
    setBlad(null);
    const p = new URLSearchParams({ od, do: doD });
    if (stanowisko) p.set('stanowisko', stanowisko);
    if (operacja) p.set('operacja', operacja);
    if (pracownik) p.set('pracownik', pracownik);
    api.get<Odp>(`/praca?${p}`).then(setD).catch((e) => setBlad(e.message)).finally(() => setLaduje(false));
  };
  useEffect(load, [od, doD, stanowisko, operacja, pracownik]);

  const ustaw = (k: string, v: string) => {
    const n = new URLSearchParams(params);
    v ? n.set(k, v) : n.delete(k);
    setParams(n, { replace: true });
  };
  const wyczysc = () => setParams(new URLSearchParams({ od, do: doD }), { replace: true });

  const igla = szukaj.trim().toLowerCase();
  const wiersze = useMemo(() => {
    const w = d?.wiersze ?? [];
    if (!igla) return w;
    return w.filter((r) =>
      r.login.toLowerCase().includes(igla) ||
      (r.zp_number ?? '').toLowerCase().includes(igla) ||
      r.serials.some((s) => s.toLowerCase().includes(igla)) ||
      r.operations.some((o) => o.toLowerCase().includes(igla)) ||
      (r.notes ?? '').toLowerCase().includes(igla));
  }, [d, igla]);

  const suma = useMemo(() => ({
    praca: wiersze.reduce((s, r) => s + r.workedMin, 0),
    norma: wiersze.reduce((s, r) => s + r.creditMin, 0),
    sztuk: new Set(wiersze.flatMap((r) => r.serials)).size,
    bezNormy: wiersze.filter((r) => r.brakNormy).length,
  }), [wiersze]);

  const nazwaOperacji = new Map(operacje.map((o) => [o.code, o.name]));

  const kolumny: Array<Kolumna<Wiersz>> = [
    { klucz: 'data', naglowek: 'Data', sort: (r) => r.work_date, szer: '104px',
      render: (r) => (
        <span className="tnum whitespace-nowrap">
          {r.work_date}
          {!!r.is_symulacja && <span className="ml-1.5"><ZnacznikSymulacji /></span>}
        </span>
      ) },
    ...(kierownik ? [{
      klucz: 'kto', naglowek: 'Login', sort: (r: Wiersz) => r.login, szer: '84px',
      render: (r: Wiersz) => <span className="font-semibold tnum text-plum">{r.login}</span>,
    }] : []),
    { klucz: 'ops', naglowek: 'Operacje',
      render: (r) => (
        <span className="flex flex-wrap gap-1">
          {r.operations.map((o) => (
            <span key={o} className="badge-neutral" title={nazwaOperacji.get(o) ?? o}>{o}</span>
          ))}
          {!!r.is_prototype && <Odznaka ton="warn">prototyp</Odznaka>}
        </span>
      ) },
    { klucz: 'zp', naglowek: 'Zlecenie', sort: (r) => r.zp_number ?? '',
      render: (r) => (
        <span>
          <span className="block whitespace-nowrap">{r.zp_number ?? '—'}</span>
          {r.serials.length > 0 && (
            <span className="block text-caption text-plum-muted tnum">
              {policz(r.serials.length, 'sztuka')}: {r.serials.slice(0, 3).join(', ')}
              {r.serials.length > 3 ? '…' : ''}
            </span>
          )}
        </span>
      ) },
    { klucz: 'godziny', naglowek: 'Godziny', sort: (r) => r.start_min, szer: '108px',
      render: (r) => (
        <span className="tnum whitespace-nowrap">
          {formatMinutes(r.start_min)}–{formatMinutes(r.end_min)}
        </span>
      ) },
    { klucz: 'czas', naglowek: 'Czas', sort: (r) => r.workedMin, prawo: true, szer: '84px',
      render: (r) => <span className="tnum">{formatDuration(r.workedMin)}</span> },
    { klucz: 'norma', naglowek: 'Norma', sort: (r) => r.creditMin, prawo: true, szer: '96px',
      render: (r) => (
        <span className="tnum">
          {formatDuration(r.creditMin)}
          {r.brakNormy && <span className="block text-caption text-warn">brak normy</span>}
        </span>
      ) },
    { klucz: 'ef', naglowek: 'Efektywność', sort: (r) => r.efficiency ?? -1, prawo: true, szer: '112px',
      render: (r) => (
        <span className={`tnum font-semibold ${
          r.efficiency === null ? 'text-plum-muted'
          : r.efficiency >= 0.9 ? 'text-ok' : r.efficiency >= 0.7 ? 'text-warn' : 'text-bad'}`}>
          {r.efficiency === null ? '—' : `${Math.round(r.efficiency * 100)}%`}
        </span>
      ) },
    { klucz: 'stan', naglowek: 'Stan', sort: (r) => r.is_finished, szer: '110px',
      render: (r) => r.is_finished
        ? <Odznaka ton="ok">zakończone</Odznaka>
        : <Odznaka>w toku</Odznaka> },
  ];

  if (blad) return <Blad tresc={blad} ponow={load} />;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-baseline gap-3">
        <h1 className="text-head-sm">Praca</h1>
        <span className="text-body-sm text-plum-muted">
          {policz(wiersze.length, 'wpis')} · {formatDecimalHours(suma.praca)} pracy · {policz(suma.sztuk, 'sztuka')}
        </span>
        {d && <span className="ml-auto text-caption text-plum-muted">dane na {d.plantTime}</span>}
      </div>

      {/* filtry — jeden wzorzec używany też na innych ekranach */}
      <div className="card-flat p-4 flex flex-wrap items-end gap-3">
        <div>
          <label className="label" htmlFor="od">Od</label>
          <input id="od" type="date" className="field tnum !w-auto" value={od} onChange={(e) => ustaw('od', e.target.value)} />
        </div>
        <div>
          <label className="label" htmlFor="do">Do</label>
          <input id="do" type="date" className="field tnum !w-auto" value={doD} max={todayInPlant()} onChange={(e) => ustaw('do', e.target.value)} />
        </div>
        <div>
          <label className="label" htmlFor="st">Stanowisko</label>
          <select id="st" className="field !w-auto" value={stanowisko} onChange={(e) => ustaw('stanowisko', e.target.value)}>
            <option value="">wszystkie</option>
            {stacje.map((s) => <option key={s.code} value={s.code}>{s.name}</option>)}
          </select>
        </div>
        <div>
          <label className="label" htmlFor="op">Operacja</label>
          <select id="op" className="field !w-auto" value={operacja} onChange={(e) => ustaw('operacja', e.target.value)}>
            <option value="">wszystkie</option>
            {operacje.map((o) => <option key={o.code} value={o.code}>{o.name}</option>)}
          </select>
        </div>
        <div className="flex-1 min-w-[200px]">
          <label className="label" htmlFor="q">Szukaj</label>
          <input id="q" className="field" placeholder="login, ZP, numer seryjny, uwagi"
                 value={szukaj} onChange={(e) => ustaw('q', e.target.value)} />
        </div>
        {pracownik && (
          <button className="badge-info" onClick={() => ustaw('pracownik', '')}
                  title="Pokaż wpisy wszystkich pracowników">
            tylko {d?.wiersze[0]?.login ?? `pracownik #${pracownik}`} <span aria-hidden>✕</span>
          </button>
        )}
        {(stanowisko || operacja || szukaj || pracownik) && (
          <button className="btn-ghost" onClick={wyczysc}>Wyczyść filtry</button>
        )}
        <div className="flex gap-2">
          <button className="btn-ghost" onClick={() => { ustaw('od', przesun(od, -7)); ustaw('do', przesun(doD, -7)); }}>◀</button>
          <button className="btn-ghost" onClick={() => { ustaw('od', przesun(od, 7)); ustaw('do', przesun(doD, 7)); }}>▶</button>
        </div>
      </div>

      {wiersze.length > 0 && (
        <div className="card p-5 grid grid-cols-2 sm:grid-cols-4 gap-5">
          <Kpi etykieta="Czas pracy" wartosc={formatDuration(suma.praca)} pod={formatDecimalHours(suma.praca)} />
          <Kpi etykieta="Wypracowana norma" wartosc={formatDuration(suma.norma)} pod={formatDecimalHours(suma.norma)} />
          <Kpi etykieta="Efektywność" wartosc={suma.praca > 0 ? `${Math.round((suma.norma / suma.praca) * 100)}%` : '—'}
               ton={suma.praca > 0 && suma.norma / suma.praca >= 0.9 ? 'ok' : 'warn'}
               pod="norma / czas zarejestrowany" />
          <Kpi etykieta="Wpisy bez normy" wartosc={suma.bezNormy}
               ton={suma.bezNormy > 0 ? 'warn' : 'neutral'}
               pod={suma.bezNormy > 0 ? 'zaniżają efektywność' : 'wszystkie mają normę'} />
        </div>
      )}

      <Tabela
        dane={wiersze}
        kolumny={kolumny}
        klucz={(r) => r.id}
        laduje={laduje}
        onKlik={setOtwarty}
        wybrany={(r) => otwarty?.id === r.id}
        pusto={
          <Pusto
            tytul="Brak wpisów w tym zakresie"
            opis={igla || stanowisko || operacja || pracownik
              ? 'Żaden wpis nie pasuje do ustawionych filtrów.'
              : 'W wybranych dniach nikt nie zarejestrował pracy.'}
            akcja={(igla || stanowisko || operacja || pracownik)
              ? <button className="btn-ghost" onClick={wyczysc}>Wyczyść filtry</button>
              : undefined}
          />
        }
      />

      {otwarty && (
        <Panel
          tytul={<span className="tnum">{otwarty.zp_number ?? 'Wpis bez zlecenia'}</span>}
          pod={`${otwarty.work_date} · ${otwarty.login}`}
          onZamknij={() => setOtwarty(null)}
        >
          <Szczegoly w={otwarty} nazwaOperacji={nazwaOperacji} />
        </Panel>
      )}
    </div>
  );
}

function Szczegoly({ w, nazwaOperacji }: { w: Wiersz; nazwaOperacji: Map<string, string> }) {
  const wiersz = (etykieta: string, wartosc: React.ReactNode) => (
    <div className="flex gap-3 py-2 border-b border-line last:border-b-0">
      <dt className="w-44 shrink-0 text-body-sm text-plum-muted">{etykieta}</dt>
      <dd className="text-body-sm text-plum-soft">{wartosc}</dd>
    </div>
  );

  return (
    <>
      {!!w.is_symulacja && (
        <p className="mb-4 badge-warn">
          To wpis symulacyjny — historia z sierpnia wgrana do pokazania analityki.
        </p>
      )}
      <div className="grid grid-cols-3 gap-4 mb-5">
        <div>
          <div className="text-label uppercase text-plum-muted mb-1">Czas</div>
          <div className="kpi-sm">{formatDuration(w.workedMin)}</div>
          <div className="text-caption text-plum-muted mt-1">{formatDecimalHours(w.workedMin)}</div>
        </div>
        <div>
          <div className="text-label uppercase text-plum-muted mb-1">Norma</div>
          <div className="kpi-sm">{formatDuration(w.creditMin)}</div>
          <div className="text-caption text-plum-muted mt-1">{formatDecimalHours(w.creditMin)}</div>
        </div>
        <div>
          <div className="text-label uppercase text-plum-muted mb-1">Efektywność</div>
          <div className={`kpi-sm ${
            w.efficiency === null ? 'text-plum-muted'
            : w.efficiency >= 0.9 ? 'text-ok' : w.efficiency >= 0.7 ? 'text-warn' : 'text-bad'}`}>
            {w.efficiency === null ? '—' : `${Math.round(w.efficiency * 100)}%`}
          </div>
        </div>
      </div>

      <dl>
        {wiersz('Pracownik', <span className="font-semibold tnum">{w.login}</span>)}
        {wiersz('Data', <span className="tnum">{w.work_date}</span>)}
        {wiersz('Godziny', <span className="tnum">{formatMinutes(w.start_min)}–{formatMinutes(w.end_min)}</span>)}
        {wiersz('Stanowisko', w.station_code)}
        {wiersz('Operacje', (
          <span className="flex flex-wrap gap-1">
            {w.operations.map((o) => <span key={o} className="badge-neutral">{nazwaOperacji.get(o) ?? o}</span>)}
          </span>
        ))}
        {wiersz('Numery seryjne', w.serials.length
          ? <span className="tnum">{w.serials.join(', ')}</span>
          : <span className="text-plum-muted">brak</span>)}
        {wiersz('Stan', w.is_finished ? <Odznaka ton="ok">zakończone</Odznaka> : <Odznaka>w toku</Odznaka>)}
        {!!w.is_prototype && wiersz('Prototyp', <Odznaka ton="warn">norma = czas rzeczywisty</Odznaka>)}
        {!!w.is_backdated && wiersz('Rejestracja', <Odznaka ton="warn">wpis wprowadzony wstecz</Odznaka>)}
        {w.brakNormy && wiersz('Uwaga', (
          <span className="text-warn">
            Część operacji nie ma normy dla tego wyrobu — ten czas obniża efektywność, choć pracownik nie zawinił.
          </span>
        ))}
        {w.notes && wiersz('Uwagi', w.notes)}
      </dl>
    </>
  );
}

function przesun(data: string, dni: number): string {
  const d = new Date(`${data}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + dni);
  return d.toISOString().slice(0, 10);
}
